'use client'

import { useState } from 'react'

import { faFileArrowUp } from '@fortawesome/pro-regular-svg-icons'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  useAnalyticsContext,
  FeatureBadge,
  InteractiveIcon,
} from '@b-stock/fe-core/bstock-react'
import type { StoreFrontDto } from '@b-stock/listing-api-client'

import type { ListingSearchState } from '@/components/listings/search/ListingSearchProvider'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { FiltersButton, FiltersButtonIcon } from '../../shared/listings-ui'
import type { ListViewSurface } from '../helpers/listViewSurface'
import { UploadBidSheetModal } from './UploadBidSheetModal'
import { useBulkBidsDiscoveryBadge } from './useBulkBidsDiscoveryBadge'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

/**
 * "Upload Bulk Bids": opens a modal to select a filled-in bid sheet CSV
 * (matching the List View table's own export columns), parse it, and
 * validate each row. Bid placement and the After-Upload Report happen
 * downstream of this component — it only gets the buyer to a validated,
 * ready-to-submit row set.
 */
export const UploadBidSheetButton = ({
  selectedListings,
  state,
  storefront,
  surface: { context, template },
}: {
  selectedListings: CombinedListingSearchResult[]
  state: ListingSearchState
  storefront: StoreFrontDto | undefined
  surface: ListViewSurface
}) => {
  const enableBidSheetUpload = useFlag(FeatureFlags.ENABLE_BID_SHEET_UPLOAD)
  const { formatMessage } = useIntl()
  const { track, trackButtonClicked } = useAnalytics()
  const { screenName } = useAnalyticsContext()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { showNewBadge, markUsed } = useBulkBidsDiscoveryBadge()

  if (!enableBidSheetUpload) return null

  const handleClick = () => {
    try {
      trackButtonClicked({
        buttonName: 'bid_sheet_upload_clicked',
        screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        context,
        template,
      })
    } catch (error) {
      console.warn(
        '[UploadBidSheetButton] bid_sheet_upload_clicked tracking failed',
        error
      )
    }
    try {
      track('bulk_bid_modal_opened', {
        screenName,
        source: AnalyticsSource.HOME_PORTAL,
        entityType: AnalyticsEntityType.BUYER,
        context,
        template,
      })
    } catch (error) {
      console.warn(
        '[UploadBidSheetButton] bulk_bid_modal_opened tracking failed',
        error
      )
    }
    setIsModalOpen(true)
  }

  return (
    <Wrapper>
      <FiltersButton appearance="secondary" onClick={handleClick}>
        <FiltersButtonIcon icon={faFileArrowUp} />
        <span>{formatMessage({ id: 'Listings.uploadBidSheet.button' })}</span>
        {showNewBadge && <FeatureBadge variant="new" />}
      </FiltersButton>
      {/* Matches the ListViewTooltip.tsx convention for an info icon +
          tooltip elsewhere in ListView, rather than a bespoke Tooltip wiring
          — native `title` tooltips don't render this much text legibly. */}
      <InteractiveIcon
        size="medium"
        variant="Info"
        tooltipProps={{ placement: 'bottom', hoverable: true }}
      >
        {formatMessage({ id: 'Listings.uploadBidSheet.tooltip' })}
      </InteractiveIcon>
      {isModalOpen && (
        <UploadBidSheetModal
          onClose={() => setIsModalOpen(false)}
          onUploadCompleted={markUsed}
          selectedListings={selectedListings}
          state={state}
          storefront={storefront}
        />
      )}
    </Wrapper>
  )
}
